import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Summary } from './Overlay';
import { Fieldset, SettingRow, Switch, Field } from './Form';
import { text } from './peopleCopy';

/*
 * Pricing — layar `pricing` paket client, dan penyimpangan terbesar sejauh ini.
 *
 * Prototipe menampilkan satu formulir: tarif dasar, tarif minimum, per
 * kilometer, per menit, kilometer gratis, biaya tunggu, komisi, tambahan malam,
 * PPN. Dari sembilan field itu, tabel Pricing hanya menyimpan dua — basePrice
 * dan multiplierValue. Sisanya tidak punya kolom di mana pun.
 *
 * Yang lebih menentukan: harga di sini BUKAN satu nilai global. Tiap baris
 * berlaku untuk satu kombinasi layanan + sub-layanan + area, dan ada 62 baris.
 * Satu formulir akan menyembunyikan 61 di antaranya dan membuat orang mengira
 * mereka mengubah harga seluruh platform padahal mengubah satu sel.
 *
 * Jadi bentuknya daftar aturan — yang justru sudah disiratkan tombol "Add rule"
 * milik prototipe sendiri. Bagian formulir globalnya tidak dibuat, dan alasannya
 * ditulis di layar, bukan cuma di commit ini.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String,
                  $categoryId: Int, $locationId: Int, $state: String) {
  getAdminPricing(currentPage: $currentPage, limit: $limit, searchList: $searchList,
                  categoryId: $categoryId, locationId: $locationId, state: $state) {
    count total status errorMessage
    areas { id name duplicateName ruleCount }
    results {
      id categoryId subCategoryId locationId serviceName subServiceName areaName
      pricingType rateUnit currency basePrice multiplierValue
      isPriceEditable isActive lockedDeleteReason
    }
  }
}`;

const Q_REF = `{
  getActiveCategories { results { id name pricingType distanceType currency } }
  getActiveSubCategories { results { id name categoryId } }
}`;

const M_SAVE = `mutation ($id: Int, $locationId: Int!, $categoryId: Int!, $subCategoryId: Int!,
                 $isActive: Boolean, $currency: String!, $isPriceEditable: Boolean,
                 $basePrice: Float, $multiplierValue: Float) {
  addUpdatePricing(id: $id, locationId: $locationId, categoryId: $categoryId,
                   subCategoryId: $subCategoryId, isActive: $isActive, currency: $currency,
                   isPriceEditable: $isPriceEditable, basePrice: $basePrice,
                   multiplierValue: $multiplierValue) { status errorMessage }
}`;

const M_STATUS = `mutation ($id: Int!, $isActive: Boolean) {
  updatePricingStatus(id: $id, isActive: $isActive) { status errorMessage }
}`;

const M_DELETE = `mutation ($id: Int!) {
  deletePricing(id: $id) { status errorMessage }
}`;

const ok = res => res && Number(res.status) === 200;

const money = (n, c) => `${c || 'AED'} ${Number(n || 0).toLocaleString('en-US',
  { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

class Pricing extends React.Component {
  state = {
    rows: [], areas: [], count: 0, total: 0, cats: [], subs: [],
    page: 1, perPage: 25, search: '', categoryId: '', locationId: '', visibility: '',
    loading: true, error: null,
    form: null, formError: null, deleting: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); this.loadRef(); }

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    categoryId: this.state.categoryId ? Number(this.state.categoryId) : undefined,
    locationId: this.state.locationId ? Number(this.state.locationId) : undefined,
    state: this.state.visibility || undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminPricing;
    if (!ok(res)) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load pricing rules.', 'تعذر تحميل قواعد التسعير.') });
      return;
    }
    this.setState({
      rows: res.results || [], areas: res.areas || [],
      count: res.count || 0, total: res.total || 0, loading: false
    });
  };

  loadRef = async () => {
    const json = await gql(Q_REF).catch(() => null);
    const d = (json && json.data) || {};
    this.setState({
      cats: (d.getActiveCategories && d.getActiveCategories.results) || [],
      subs: (d.getActiveSubCategories && d.getActiveSubCategories.results) || []
    });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  pick = key => e => this.setState({ [key]: e.target.value, page: 1 }, () => this.load());

  catOf = id => this.state.cats.find(c => String(c.id) === String(id)) || {};

  openAdd = () => this.setState({
    form: {
      id: null, categoryId: this.state.categoryId || '', subCategoryId: '',
      locationId: this.state.locationId || '', basePrice: 0, multiplierValue: 1,
      currency: 'AED', isPriceEditable: false, isActive: true
    },
    formError: null, notice: null
  });

  openEdit = r => this.setState({
    form: {
      id: r.id, categoryId: r.categoryId, subCategoryId: r.subCategoryId,
      locationId: r.locationId, basePrice: r.basePrice, multiplierValue: r.multiplierValue,
      currency: r.currency || 'AED', isPriceEditable: r.isPriceEditable, isActive: r.isActive
    },
    formError: null, notice: null
  });

  setForm = (key, value) => this.setState(st => {
    const form = { ...st.form, [key]: value };
    // Mengganti layanan membuat sub-layanan yang terpilih tidak lagi masuk akal.
    if (key === 'categoryId') form.subCategoryId = '';
    return { form };
  });

  submit = async () => {
    const f = this.state.form;
    if (!f.categoryId || !f.subCategoryId || !f.locationId) {
      this.setState({ formError: this.tx('Service, sub-service and area are all required.', 'الخدمة والخدمة الفرعية والمنطقة مطلوبة.') });
      return;
    }
    this.setState({ saving: true, formError: null });

    const json = await gql(M_SAVE, {
      id: f.id || undefined,
      categoryId: Number(f.categoryId),
      subCategoryId: Number(f.subCategoryId),
      locationId: Number(f.locationId),
      currency: f.currency || 'AED',
      basePrice: Number(f.basePrice) || 0,
      multiplierValue: Number(f.multiplierValue) || 0,
      isPriceEditable: !!f.isPriceEditable,
      isActive: !!f.isActive
    }).catch(() => null);
    const res = json && json.data && json.data.addUpdatePricing;
    this.setState({ saving: false });
    if (!ok(res)) {
      this.setState({ formError: (res && res.errorMessage) || this.tx('Could not save the rule.', 'تعذر حفظ القاعدة.') });
      return;
    }
    this.setState({
      form: null,
      notice: {
        tone: 'info',
        text: (f.id ? this.tx('Rule updated. ', 'تم تحديث القاعدة. ') : this.tx('Rule created. ', 'تم إنشاء القاعدة. '))
          + this.tx('Bookings already in progress keep the price they were quoted.', 'الحجوزات الجارية تحتفظ بالسعر الذي عُرض عليها.')
      }
    });
    this.load(true);
  };

  toggleStatus = async r => {
    this.setState({ saving: true, notice: null });
    const json = await gql(M_STATUS, { id: r.id, isActive: !r.isActive }).catch(() => null);
    const res = json && json.data && json.data.updatePricingStatus;
    this.setState({ saving: false });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not change the status.', 'تعذر تغيير الحالة.') }
        : {
          tone: 'info',
          text: r.isActive
            ? `${r.subServiceName} ${this.tx('in', 'في')} ${r.areaName} ${this.tx('can no longer be priced, so it stops being bookable there.', 'لم يعد لها سعر، لذلك تتوقف إتاحتها للحجز هناك.')}`
            : `${r.subServiceName} ${this.tx('in', 'في')} ${r.areaName} ${this.tx('is priced again.', 'تم تسعيرها مرة أخرى.')}`
        }
    });
    this.load(true);
  };

  remove = async () => {
    const r = this.state.deleting;
    this.setState({ saving: true });
    const json = await gql(M_DELETE, { id: r.id }).catch(() => null);
    const res = json && json.data && json.data.deletePricing;
    this.setState({ saving: false, deleting: null });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not delete the rule.', 'تعذر حذف القاعدة.') }
        : { tone: 'danger', text: `${this.tx('The rule for', 'تم حذف قاعدة')} ${r.subServiceName} ${this.tx('in', 'في')} ${r.areaName}.` }
    });
    this.load(true);
  };

  columns() {
    const t = this.tx;
    return [
      { key: 'serviceName', label: t('Service', 'الخدمة'), sortable: true },
      {
        key: 'subServiceName', label: t('Sub-service', 'الخدمة الفرعية'), sortable: true,
        render: r => <b>{r.subServiceName || <span className="muted">{t('Missing', 'مفقود')}</span>}</b>
      },
      {
        key: 'areaName', label: t('Area', 'المنطقة'), sortable: true,
        render: r => r.areaName || <span className="muted">{t('Missing', 'مفقود')}</span>
      },
      {
        key: 'basePrice', label: t('Base fare', 'الأجرة الأساسية'), align: 'end', sortable: true,
        render: r => <span className="num">{money(r.basePrice, r.currency)}</span>
      },
      {
        // Angka telanjang tanpa satuan tidak bisa dibaca: artinya berbeda per
        // layanan, dan pada layanan bertarif tetap tidak dipakai sama sekali.
        key: 'multiplierValue', label: t('Rate', 'المعدل'), align: 'end', sortable: true,
        render: r => r.rateUnit
          ? (
            <span className="num">
              {money(r.multiplierValue, r.currency)}
              <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}> {r.rateUnit}</span>
            </span>
          )
          : <span className="muted">{t('Flat fare', 'أجرة ثابتة')}</span>
      },
      {
        key: 'isPriceEditable', label: t('Provider may edit', 'يمكن لمقدم الخدمة التعديل'),
        render: r => r.isPriceEditable
          ? <span className="tag warn"><i />{t('Editable on site', 'قابل للتعديل في الموقع')}</span>
          : <span className="muted">{t('Fixed', 'ثابت')}</span>
      },
      {
        key: 'isActive', label: t('Status', 'الحالة'),
        render: r => (
          <span className={`tag ${r.isActive ? 'ok' : ''}`}>
            <i />{r.isActive ? t('Live', 'نشط') : t('Off', 'متوقف')}
          </span>
        )
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title="Edit rule"
              aria-label={`Edit rule ${r.id}`} onClick={() => this.openEdit(r)}>
              <Icon name="edit" />
            </button>
            <button className="btn icon" type="button"
              title={r.isActive ? 'Take this rule off' : 'Put this rule live'}
              aria-label={`Toggle rule ${r.id}`}
              onClick={() => this.toggleStatus(r)}>
              <Icon name={r.isActive ? 'x' : 'check'} />
            </button>
            <button className="btn icon" type="button"
              title={r.lockedDeleteReason || 'Delete rule'}
              aria-label={`Delete rule ${r.id}`}
              disabled={!!r.lockedDeleteReason}
              onClick={() => this.setState({ deleting: r, notice: null })}>
              <Icon name="trash" />
            </button>
          </span>
        )
      }
    ];
  }

  areaLabel = a => `${a.name}${a.duplicateName ? ` (#${a.id})` : ''}`;

  renderForm() {
    const f = this.state.form;
    const { saving, formError, cats, subs, areas } = this.state;
    const t = this.tx;
    const cat = this.catOf(f.categoryId);
    const mine = subs.filter(s => String(s.categoryId) === String(f.categoryId));
    const unit = cat.pricingType === 'distance'
      ? `per ${cat.distanceType === 'miles' ? 'mile' : 'km'}`
      : cat.pricingType === 'hourly' ? 'per hour' : null;

    return (
      <Modal
        icon="coins" wide
        title={f.id ? t('Edit pricing rule', 'تعديل قاعدة التسعير') : t('Add pricing rule', 'إضافة قاعدة تسعير')}
        sub={t('One rule covers one service, one sub-service and one area.', 'تغطي كل قاعدة خدمة وخدمة فرعية ومنطقة واحدة.')}
        confirmLabel={f.id ? t('Save rule', 'حفظ القاعدة') : t('Create rule', 'إنشاء القاعدة')}
        busy={saving}
        disabled={!f.categoryId || !f.subCategoryId || !f.locationId}
        onConfirm={this.submit}
        onClose={() => this.setState({ form: null, formError: null })}
      >
        {formError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{formError}</span>
          </div>
        )}

        <Fieldset title={t('Applies to', 'ينطبق على')}>
          <Field label={t('Service', 'الخدمة')} required>
            <select className="inp" value={f.categoryId}
              onChange={e => this.setForm('categoryId', e.target.value)}>
              <option value="">{t('Choose a service…', 'اختر خدمة…')}</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label={t('Sub-service', 'الخدمة الفرعية')} required
            hint={f.categoryId && !mine.length ? t('This service has no active sub-services.', 'لا توجد خدمات فرعية نشطة لهذه الخدمة.') : null}>
            <select className="inp" value={f.subCategoryId} disabled={!f.categoryId}
              onChange={e => this.setForm('subCategoryId', e.target.value)}>
              <option value="">{t('Choose a sub-service…', 'اختر خدمة فرعية…')}</option>
              {mine.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label={t('Area', 'المنطقة')} required span
            hint={t('Only areas that already carry a rule are listed here.', 'تظهر هنا المناطق التي لديها قاعدة بالفعل فقط.') }>
            <select className="inp" value={f.locationId}
              onChange={e => this.setForm('locationId', e.target.value)}>
              <option value="">{t('Choose an area…', 'اختر منطقة…')}</option>
              {areas.map(a => (
                <option key={a.id} value={a.id}>{this.areaLabel(a)}</option>
              ))}
            </select>
          </Field>
        </Fieldset>

        <Fieldset title={t('Fare', 'الأجرة')}>
          <Field label={`${t('Base fare', 'الأجرة الأساسية')} (${f.currency})`} required
            hint={t('Charged on every booking of this combination.', 'تُحصّل في كل حجز لهذا المزيج.') }>
            <input className="inp" type="number" min="0" step="0.01" value={f.basePrice}
              onChange={e => this.setForm('basePrice', e.target.value)} />
          </Field>
          <Field
            label={unit ? `${t('Rate', 'المعدل')} ${unit} (${f.currency})` : t('Rate multiplier', 'مضاعف المعدل')}
            hint={unit
              ? t('Added to the base fare for each unit.', 'تُضاف إلى الأجرة الأساسية لكل وحدة.')
              : t('This service is priced at a flat fare, so this value is not used.', 'هذه الخدمة بأجرة ثابتة، لذلك لا تُستخدم هذه القيمة.') }>
            <input className="inp" type="number" min="0" step="0.01" value={f.multiplierValue}
              disabled={!unit}
              onChange={e => this.setForm('multiplierValue', e.target.value)} />
          </Field>
        </Fieldset>

        <Fieldset title={t('Rules', 'القواعد')} rows>
          <SettingRow label={t('Provider may change the price on site', 'يمكن لمقدم الخدمة تغيير السعر في الموقع')}
            hint={t('Lets the provider quote a different amount once they see the vehicle.', 'يسمح لمقدم الخدمة بتقديم مبلغ مختلف بعد رؤية المركبة.') }>
            <Switch checked={f.isPriceEditable} label={t('Provider may change the price', 'يمكن لمقدم الخدمة تغيير السعر')}
              onChange={v => this.setForm('isPriceEditable', v)} />
          </SettingRow>
          <SettingRow label={t('Live', 'نشط')}
            hint={t('A rule that is off means the sub-service cannot be booked in that area at all.', 'إيقاف القاعدة يمنع حجز الخدمة الفرعية في تلك المنطقة.') }>
            <Switch checked={f.isActive} label={t('Live', 'نشط')}
              onChange={v => this.setForm('isActive', v)} />
          </SettingRow>
        </Fieldset>
      </Modal>
    );
  }

  render() {
    const {
      rows, areas, count, total, cats, page, perPage, loading, error,
      categoryId, locationId, visibility, form, deleting, saving, notice
    } = this.state;
    const t = this.tx;

    return (
      <AdminShell
        current="pricing"
        breadcrumb={[t('Catalogue', 'الكتالوج'), t('Pricing', 'التسعير')]}
        title={t('Pricing', 'التسعير')}
        lead={this.locale() === 'ar'
          ? `${total} قاعدة أجرة عبر جميع الخدمات والمناطق. التغييرات تطبق على الحجوزات الجديدة فقط.`
          : `${total} fare rule${total === 1 ? '' : 's'} across every service and area. Changes apply to new bookings only.`}
        actions={[
          <a className="btn ghost" href="/siteadmin/pricing" key="c">{t('Open in classic', 'فتح النسخة الكلاسيكية')}</a>,
          <button className="btn" type="button" key="a" onClick={this.openAdd}>
            <Icon name="plus" />{t('Add rule', 'إضافة قاعدة')}
          </button>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t('Dismiss', 'إخفاء')}</button>
          </div>
        )}

        {/* Menyebutkan penyimpangan di tempat orang membacanya, bukan cuma di
            riwayat git. Komisi memang ada, tapi per layanan — dan layar
            Services yang memilikinya. */}
        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">
            {t('A fare is a base amount plus a rate, set per service, sub-service and area. Minimum fare, per-minute charges, included kilometres, waiting time, night surcharge and VAT are not stored anywhere in this system and are not shown here. Platform commission is set per service on the Services screen.', 'الأجرة مبلغ أساسي مع معدل، وتُحدد لكل خدمة وخدمة فرعية ومنطقة. الحد الأدنى للأجرة ورسوم الدقيقة والكيلومترات المجانية ووقت الانتظار والرسوم الليلية وضريبة القيمة المضافة غير مخزنة في النظام ولا تعرض هنا. تُحدد عمولة المنصة لكل خدمة في شاشة الخدمات.')}
          </span>
        </div>

        {form && this.renderForm()}

        {deleting && (
          <Modal
            icon="trash" tone="danger"
            title={t('Delete this pricing rule?', 'حذف قاعدة التسعير هذه؟')}
            sub={`${deleting.subServiceName} in ${deleting.areaName}`}
            confirmLabel={t('Delete rule', 'حذف القاعدة')}
            busy={saving}
            onConfirm={this.remove}
            onClose={() => this.setState({ deleting: null })}
          >
            <Summary pairs={[
              [t('Service', 'الخدمة'), deleting.serviceName],
              [t('Sub-service', 'الخدمة الفرعية'), deleting.subServiceName],
              [t('Area', 'المنطقة'), deleting.areaName],
              [t('Base fare', 'الأجرة الأساسية'), money(deleting.basePrice, deleting.currency)]
            ]} />
            <div className="notice danger" style={{ marginBottom: 0 }}>
              <span className="ni">!</span>
              <span className="nt">
                {t('With no rule, this sub-service cannot be booked in this area at all. To pause it and keep the numbers, take it off instead of deleting.', 'بدون هذه القاعدة لا يمكن حجز هذه الخدمة الفرعية في المنطقة. لإيقافها مع الاحتفاظ بالأرقام، عطّلها بدلاً من حذفها.')}
              </span>
            </div>
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t('Search service, sub-service or area', 'ابحث عن الخدمة أو الخدمة الفرعية أو المنطقة')}
              filters={
                <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
                  <select className="inp" value={categoryId} onChange={this.pick('categoryId')}
                    aria-label={t('Filter by service', 'تصفية حسب الخدمة')}>
                    <option value="">{t('Service: all', 'الخدمة: الكل')}</option>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select className="inp" value={locationId} onChange={this.pick('locationId')}
                    aria-label={t('Filter by area', 'تصفية حسب المنطقة')}>
                    <option value="">{t('Area: all', 'المنطقة: الكل')}</option>
                    {areas.map(a => (
                      <option key={a.id} value={a.id}>
                        {this.areaLabel(a)} ({a.ruleCount})
                      </option>
                    ))}
                  </select>
                  <select className="inp" value={visibility} onChange={this.pick('visibility')}
                    aria-label={t('Filter by status', 'تصفية حسب الحالة')}>
                    <option value="">{t('Status: all', 'الحالة: الكل')}</option>
                    <option value="active">{t('Live', 'نشط')}</option>
                    <option value="inactive">{t('Off', 'متوقف')}</option>
                  </select>
                </span>
              }
              emptyText={t('No pricing rules match', 'لا توجد قواعد تسعير مطابقة')}
              emptyHint={t('Try clearing the service or area filter.', 'جرّب مسح تصفية الخدمة أو المنطقة.')}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(Pricing);
